"use client";

import * as React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { Maximize2 } from "lucide-react";
// import useEmblaCarousel from "embla-carousel-react";

interface SlideShowProps {
  directory?: string;
  slideCount?: number;
  showSlideCount?: boolean;
  showNavigationButtons?: boolean;
}

export default function Slideshow({
  directory = "default-slides",
  slideCount = 4,
  showSlideCount = true,
  showNavigationButtons = true,
}: SlideShowProps) {
  const [current, setCurrent] = React.useState(0);
  const [isExpanded, setIsExpanded] = React.useState(false);
  const [compactEmbla, setCompactEmbla] = React.useState<any>(null);
  const [expandedEmbla, setExpandedEmbla] = React.useState<any>(null);

  const slides = React.useMemo(
    () =>
      Array.from(
        { length: slideCount },
        (_, i) => `${directory}${i + 1}.png?v=${Math.random()}`
      ),
    [directory, slideCount]
  );

  const onSelect = React.useCallback((embla: any) => {
    setCurrent(embla.selectedScrollSnap());
  }, []);

  React.useEffect(() => {
    if (compactEmbla) {
      compactEmbla.on("select", () => onSelect(compactEmbla));
    }
  }, [compactEmbla, onSelect]);

  React.useEffect(() => {
    if (expandedEmbla) {
      expandedEmbla.on("select", () => onSelect(expandedEmbla));
      expandedEmbla.scrollTo(current, { immediate: true });
    }
  }, [expandedEmbla, current, onSelect]);

  const CarouselComponent = React.useCallback(
    ({ expanded }: { expanded: boolean }) => (
      <div className="relative overflow-hidden border">
        <Carousel
          opts={{
            align: "start",
            loop: true,
            startIndex: current,
          }}
          setApi={expanded ? setExpandedEmbla : setCompactEmbla}
          className={`w-full ${expanded ? "max-w-5xl mx-auto" : ""}`}
        >
          <CarouselContent>
            {slides.map((slide, index) => (
              <CarouselItem key={index}>
                <div className="overflow-hidden">
                  <div className="relative aspect-[16/9]">
                    <img
                      src={slide}
                      alt={`Slide ${index + 1}`}
                      className="absolute inset-0 w-full h-full object-contain"
                    />
                  </div>
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>
          <CarouselPrevious style={{ left: "-5px" }} />
          <CarouselNext style={{ right: "-5px" }} />
        </Carousel>
      </div>
    ),
    [slides, current]
  );

  return (
    <Card className="w-full border-2 border-gray-200">
      <CardHeader className="bg-muted/50 px-6 py-3">
        <div className="flex justify-between items-center">
          <CardTitle className="text-md">Lesson slides</CardTitle>

          <Dialog open={isExpanded} onOpenChange={setIsExpanded}>
            <DialogTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="bg-background/80 backdrop-blur-sm"
                onClick={() => setIsExpanded(true)}
              >
                <Maximize2 className="h-4 w-4" />
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-7xl w-full">
              <CarouselComponent expanded={true} />
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>

      <CardContent>
        <CarouselComponent expanded={false} />
        {showSlideCount && (
          <div className="py-2 text-center text-sm text-muted-foreground">
            Slide {current + 1} of {slideCount}
          </div>
        )}
        {showNavigationButtons && (
          <div className="flex justify-center space-x-2 mt-4">
            {slides.map((_, index) => (
              <Button
                key={index}
                variant="outline"
                size="sm"
                className={`w-8 h-8 p-0 ${
                  current === index ? "bg-primary text-primary-foreground" : ""
                }`}
                onClick={() => {
                  setCurrent(index);
                  compactEmbla?.scrollTo(index);
                  expandedEmbla?.scrollTo(index, { immediate: true });
                }}
              >
                {index + 1}
              </Button>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
